import { useEffect, useRef, useState } from 'react'

const DEFAULT_PAGE_SIZE = 20

/**
 * Progressively reveals a long, already-loaded list as the user scrolls, so a
 * column with hundreds of cards doesn't render them all at once. Attach
 * `sentinelRef` to an element at the end of the list — once it scrolls into
 * view another page is revealed, until everything is shown.
 */
export function useIncrementalReveal<T>(items: T[], pageSize = DEFAULT_PAGE_SIZE) {
  const [count, setCount] = useState(pageSize)
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const hasMore = count < items.length

  useEffect(() => {
    if (!hasMore) return
    const node = sentinelRef.current
    if (!node) return
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setCount((current) => current + pageSize)
      }
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, pageSize])

  return { visible: items.slice(0, count), hasMore, sentinelRef }
}
